import { Box, Flex, Text, Tooltip } from "@mantine/core";
import get from "lodash/get";
import { useEffect, useRef, useState } from "react";

import { theme } from "../../../utils/mantine/theme.ts";
import { UploadedDocument } from "../types";
import { UploadDocumentMessage } from "../UploadDocumentMessage.tsx";
import { useStyles } from "./DocumentPreview.styles";

interface DocumentPreviewProps {
  document?: UploadedDocument | null;
}

const DocumentPreview = ({ document }: DocumentPreviewProps) => {
  const { classes } = useStyles();
  const [scale, setScale] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [showDragHelper, setShowDragHelper] = useState(true);

  const isPdf = document?.filePath.toLowerCase().endsWith(".pdf");

  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setShowDragHelper(true);
  }, [document?.id]);

  useEffect(() => {
    if (showDragHelper) {
      const timer = setTimeout(() => {
        setShowDragHelper(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showDragHelper]);

  const handleZoomIn = () => {
    setScale((prevScale) => Math.min(prevScale + 0.2, 3));
  };

  const handleZoomOut = () => {
    setScale((prevScale) => Math.max(prevScale - 0.2, 0.5));
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
    setStartPos({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragging) return;

    setPosition({
      x: e.clientX - startPos.x,
      y: e.clientY - startPos.y,
    });

    if (showDragHelper) {
      setShowDragHelper(false);
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!dragging || e.touches.length !== 1) return;

    setPosition({
      x: e.touches[0].clientX - startPos.x,
      y: e.touches[0].clientY - startPos.y,
    });

    if (showDragHelper) {
      setShowDragHelper(false);
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  const handleTouchEnd = () => {
    setDragging(false);
  };

  const handleMouseLeave = () => {
    setDragging(false);
  };

  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  if (!document) {
    return <UploadDocumentMessage />;
  }

  return (
    <Flex
      ref={containerRef}
      className={classes.container}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {showDragHelper && (
        <div className={classes.dragHelper}>
          <Flex gap={5} direction="column" className={classes.helperBox}>
            <Text size="lg">Click and drag to move the document</Text>
            <Text size="sm" color={get(theme, "colors.dusk.0", "dusk")}>
              Use + and - buttons to zoom in/out
            </Text>
            {isPdf && (
              <Text size="sm" color={get(theme, "colors.dusk.0", "dusk")}>
                Use the PDF viewer controls to navigate pages
              </Text>
            )}
          </Flex>
        </div>
      )}

      <div
        className={classes.documentContainer}
        style={{
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px)) scale(${scale})`,
          transition: dragging ? "none" : "transform 0.3s ease",
          cursor: dragging ? "grabbing" : "grab",
          width: "100%",
          height: "100%",
        }}
        onMouseDown={handleMouseDown}
      >
        {isPdf ? (
          <div className={classes.pdfWrapper}>
            <iframe
              src={document.fileUrl}
              className={classes.iframe}
              title="PDF Document"
            />
            <Text
              size="sm"
              color="dimmed"
              align="center"
              className={classes.pdfNote}
            >
              Note: Use the PDF viewers built-in controls for page navigation
            </Text>
          </div>
        ) : (
          <img
            src={document.fileUrl}
            alt={document.fileName}
            className={classes.image}
          />
        )}
      </div>

      {!isPdf && (
        <Box className={classes.controlsBox}>
          <Tooltip label="Zoom out" position="top">
            <button className={classes.controlButton} onClick={handleZoomOut}>
              −
            </button>
          </Tooltip>
          <Tooltip label="Zoom in" position="top">
            <button className={classes.controlButton} onClick={handleZoomIn}>
              +
            </button>
          </Tooltip>
          <Tooltip label="Reset view" position="top">
            <button className={classes.resetButton} onClick={handleReset}>
              Reset
            </button>
          </Tooltip>
        </Box>
      )}
    </Flex>
  );
};

export default DocumentPreview;
